import { useLazyQuery } from '@apollo/client';
import { Button } from '@mui/material';
import type { GridPaginationModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState, type FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPagination } from '~/components/data-grid';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { useTotalCount } from '~/hooks/pagination';
import {
  DataItemCellSelectGetDocument,
  type DataItemCellSelect__DataItemListed as DataItem,
  type DataItemCellSelect__DataSourceListed as DataSourceListed,
} from '../DataItemCellSelect.generated';
import {
  SpreadsheetyDataGrid,
  useDataSourceTable,
  type DataItemData,
  type SelectedCellParams,
  type SelectedColumnParams,
} from '../lib';
import { DataItemCellSelectToolbar } from './';

export interface DataItemCellSelectModalProps {
  dataItem: DataItem | null;
  dataItemKey: string | undefined;
  dataSourceId?: number;
  dataSources: ReadonlyArray<DataSourceListed>;
  deviceAware?: boolean;
  onChange: (selected: SelectedCellParams | SelectedColumnParams | undefined) => void;
  onClose: () => void;
}

export const DataItemCellSelectModal = ({
  dataItem,
  dataItemKey,
  dataSourceId,
  dataSources,
  deviceAware = false,
  onChange,
  onClose,
}: DataItemCellSelectModalProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [selectedCellParams, setSelectedCellParams] = useState<SelectedCellParams | undefined>(
    dataItem && dataItemKey ? { id: String(dataItem.id), field: dataItemKey } : undefined,
  );
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>(
    deviceAware ? dataItemKey : undefined,
  );
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(
    dataSourceId ?? dataItem?.source.id ?? dataSources.at(0)?.id ?? null,
  );
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [getDataSource, selectedSourceQuery] = useLazyQuery(DataItemCellSelectGetDocument);

  useEffect(() => {
    if (!selectedSourceId) return;
    void getDataSource({
      variables: {
        dataSourceId: selectedSourceId,
        networkId: currentNetwork.id,
        orderFirst: dataItem?.id,
        page,
        perPage: 50,
        search,
      },
    });
  }, [currentNetwork.id, dataItem?.id, getDataSource, page, search, selectedSourceId]);

  const onPaginationModelChange = useCallback(
    ({ page }: GridPaginationModel) => {
      setPage(page + 1);
    },
    [setPage],
  );

  const dataSource = useMemo(
    () => selectedSourceQuery.data?.network?.dataSource,
    [selectedSourceQuery],
  );

  // The column the device match runs on. Only device-aware mode treats it specially: a cell
  // binding to it is still a legitimate choice.
  const keyColumn = deviceAware ? dataSource?.deviceKey ?? undefined : undefined;

  const handleCellFocus = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const { currentTarget } = event;
      const row = currentTarget.parentElement;
      if (!row) return;

      const id = row.dataset.id;
      const field = currentTarget.dataset.field;

      // Device-aware bindings take the column; an empty sample cell still names one.
      if (deviceAware) {
        if (field && field !== keyColumn) setSelectedColumn(field);
        return;
      }

      const data: DataItemData | null | undefined = dataSource?.items.nodes.find(
        (x) => String(x.id) === id,
      )?.data;
      if (!id || !field || !data || !data[field]) return;

      setSelectedCellParams({ id, field, value: data[field] });
    },
    [dataSource?.items.nodes, deviceAware, keyColumn],
  );

  const { columns, rows } = useDataSourceTable(
    dataSource,
    selectedCellParams,
    selectedColumn,
    keyColumn,
  );
  const totalCount = useTotalCount(dataSource?.items.pageInfo.nodesCount);

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open>
      <DialogTitle onClose={onClose}>
        {deviceAware
          ? t('components.data_item_cell_select.dialog.column_title')
          : t('components.data_item_cell_select.dialog.title')}
      </DialogTitle>
      <DialogContent sx={{ overflow: 'hidden' }}>
        <DialogContentTitle>
          {deviceAware
            ? t('components.data_item_cell_select.dialog.column_description')
            : t('components.data_item_cell_select.dialog.description')}
        </DialogContentTitle>
        <DataItemCellSelectToolbar
          dataSources={dataSources}
          onSearch={(x) => setSearch(x)}
          onSourceSelect={(x) => {
            setSelectedSourceId(() => x);
            // A column name only means something within the source it was picked from.
            setSelectedColumn(undefined);
          }}
          rowCount={rows.length}
          search={search}
          selectedSourceId={selectedSourceId}
        />
        <SpreadsheetyDataGrid
          columns={columns}
          disableColumnMenu
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: { pageSize: 50, page: 0 },
            },
          }}
          loading={selectedSourceQuery.loading}
          onColumnHeaderClick={
            deviceAware
              ? ({ field }) => {
                  if (field !== keyColumn) setSelectedColumn(field);
                }
              : undefined
          }
          onPaginationModelChange={onPaginationModelChange}
          pagination
          paginationMode="server"
          paginationModel={{ page: page - 1, pageSize: 50 }}
          rowCount={totalCount}
          rows={rows}
          slots={{ pagination: DataGridPagination }}
          slotProps={{
            cell: { onFocus: handleCellFocus },
            loadingOverlay: { variant: 'skeleton' },
            pagination: { rowsPerPageOptions: [] },
          }}
          sx={{
            '--DataGrid-overlayHeight': '300px',
            height: '60vh',
            // Picking a column: the heading is a target, and an empty sample cell still names one.
            ...(deviceAware && {
              '& .MuiDataGrid-columnHeader': { cursor: 'pointer' },
              '& .MuiDataGrid-cell:has(.empty)': { cursor: 'pointer' },
              '& .MuiDataGrid-columnHeader.key, & .MuiDataGrid-cell:has(.key)': {
                cursor: 'not-allowed',
                opacity: 0.55,
              },
            }),
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={
            deviceAware
              ? !selectedColumn ||
                !selectedSourceId ||
                !dataSource?.keys.includes(selectedColumn) ||
                selectedColumn === keyColumn ||
                (dataSourceId === selectedSourceId && dataItemKey === selectedColumn)
              : (!dataItem && !dataItemKey && !selectedCellParams) ||
                (dataItem?.id === selectedCellParams?.id &&
                  dataItemKey === selectedCellParams?.field)
          }
          onClick={() => {
            if (deviceAware) {
              onChange(
                selectedColumn && selectedSourceId
                  ? { dataSourceId: selectedSourceId, field: selectedColumn }
                  : undefined,
              );
            } else {
              onChange(selectedCellParams);
            }
            onClose();
          }}
          type="submit"
          variant="contained"
        >
          {t('components.data_item_cell_select.dialog.select')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
